import React, { ReactNode, useEffect } from 'react';
import {
  MobileMode,
  MobileThemeProvider,
  Theme,
  ThemeMode,
  ThemeProvider,
} from '@amzn/storm-ui';
import { StyleSheetManager, useTheme } from 'styled-components';
import { connect } from 'react-redux';
import rtlPlugin from 'stylis-plugin-rtl';
import { ErrorBoundary } from '../ErrorBoundary';
import { RootState } from 'src/app/store';
import {
  PAGE_LAYOUT_TYPE,
  selectPageLayoutType,
  selectViewType,
  VIEW_TYPE,
} from 'src/app/slice';
import { getExtendedTheme } from './ChromeAppUtils';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { ATC_EXTERNAL_TOGGLE } from 'src/commons/constants'; // This noop plugin is required to change hasing algo for styled component

// This noop plugin is required to change hasing algo for styled component
// and is need to prevent classname collisions

function NoopPlugin() {}

Object.defineProperty(NoopPlugin, 'name', { value: 'aac-ccx' });

const BodyBackgroundSetter: React.FC = () => {
  const theme = useTheme() as Theme;

  useEffect(() => {
    document.body.style.backgroundColor =
      theme.themeMode === ThemeMode.DARK
        ? theme.surface.subtleOffset
        : theme.surface.default;
  }, [theme.themeMode]);

  return null;
};

// The common HOC for Chrome App
interface ChromeAppProps {
  name: string;
  children: ReactNode;
  mobileView?: boolean;
  viewType: VIEW_TYPE;
  pageLayoutType: PAGE_LAYOUT_TYPE;
}

export const ChromeApp: React.FC<ChromeAppProps> = ({
  name,
  mobileView,
  children,
  viewType,
  pageLayoutType,
}) => {
  const { loadingStatus, config } = useInternalChromeConfigContext();
  if (loadingStatus === 'loading') return null;

  const atcExternalMode =
    window.sessionStorage.getItem(ATC_EXTERNAL_TOGGLE) === 'true';
  if (
    config.isInternalView &&
    pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD &&
    atcExternalMode
  ) {
    window.sessionStorage.setItem(ATC_EXTERNAL_TOGGLE, 'false');
  }

  return (
    <ErrorBoundary name={name} mobileView={mobileView}>
      <ThemeProvider
        theme={(theme) => getExtendedTheme(theme, viewType)}
        enableThemeModes
      >
        <MobileThemeProvider
          mobileMode={
            viewType === VIEW_TYPE.MOBILE
              ? MobileMode.Enable
              : MobileMode.Disable
          }
        >
          <BodyBackgroundSetter />
          <StyleSheetManager
            stylisPlugins={
              document.documentElement.dir === 'rtl'
                ? [rtlPlugin, NoopPlugin]
                : [NoopPlugin]
            }
          >
            {children}
          </StyleSheetManager>
        </MobileThemeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

const connector = connect((state: RootState, ownProps) => {
  return {
    ...ownProps,
    viewType: selectViewType(state),
    pageLayoutType: selectPageLayoutType(state),
  };
});

export const ConnectedChromeApp = connector(ChromeApp);
